import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-router-dom";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";
import { CREATE_CPA_SCHEMA } from "../../utils/schemas/accounts/createCpaSchema";
import { generateGenericFormState } from "../../utils/generators/states/forms/genericFormState";
import getCreateCpaSchemas from "../../utils/validation/createCpaValidation";
import { renderField as dynamicRenderField } from "../../utils/forms/renderers/DynamicFieldRenderer";
import FormSectionWrapper from "../ui/account/FormSectionWrapper";
import VerificationModal from "../ui/popup/VerificationModal";
import TermsConsentNotice from "../ui/auth/TermsConsentNotice";
import { store } from "../../store";
import { loginRdx } from "../../store/redux_auth/authSlice";
import { associateAuthenticatedInstallationIfNative } from "../../utils/native/installationAssociation";

function CreateCpaForm() {
  const navigate = useNavigate();
  const [currentErrors, setCurrentErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupStep, setPopupStep] = useState("loading"); // "loading", "verification", "error", or "success"
  const [userEmail, setUserEmail] = useState("");
  const [storedFormData, setStoredFormData] = useState(null); // Store form data after first call

  // Force re-render on form value changes to reflect UI immediately (e.g., checkboxes)
  const [, setRenderTick] = useState(0);

  const { Personal_Info = [] } = CREATE_CPA_SCHEMA || {};
  const schemaSections = Personal_Info;

  // Generate schema dynamically from CREATE_CPA_SCHEMA
  const { completeSchema: cpaFormSchema } = getCreateCpaSchemas();

  // Initialize react-hook-form
  const formMethods = useForm({
    resolver: zodResolver(cpaFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [initialFormState, setInitialFormState] = useState(null);

  useEffect(() => {
    const generated = generateGenericFormState({ schemaSections });
    setInitialFormState(generated);

    // Reset form with initial values when they're ready
    if (generated && Object.keys(generated).length > 0) {
      formMethods.reset(generated);
    }
  }, [schemaSections, formMethods]);

  // Re-validate when form values change so isValid stays in sync
  useEffect(() => {
    const subscription = formMethods.watch(() => {
      void formMethods.trigger(undefined, { shouldFocus: false });
      setRenderTick((t) => t + 1);
    });
    return () => subscription.unsubscribe();
  }, [formMethods]);

  const isEmailError = (rawErrors, messages) => {
    if (
      Array.isArray(rawErrors?.custom_user?.email) &&
      rawErrors.custom_user.email.length > 0
    )
      return true;
    if (
      Array.isArray(rawErrors?.errors?.email) &&
      rawErrors.errors.email.length > 0
    )
      return true;
    if (
      typeof rawErrors?.message === "string" &&
      /email/i.test(rawErrors.message)
    )
      return true;
    return Array.isArray(messages) && messages.some((m) => /email/i.test(m));
  };

  const focusFieldByName = (name) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`input[name="${name}"]`);
      if (el) {
        el.focus();
        if (typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    });
  };

  const handleSubmit = async (data) => {
    // Clear current errors on new submission
    setCurrentErrors([]);
    setIsSubmitting(true);
    setIsPopupOpen(true);
    setPopupStep("loading");
    setUserEmail(data.email || "");

    // Store form data in state for security (won't send again until verification)
    const formDataToStore = {
      firstname: data.firstname || "",
      lastname: data.lastname || "",
      email: data.email || "",
      password: data.password || "",
      license_type: data.license_type || "",
    };
    setStoredFormData(formDataToStore);

    try {
      // First call: verify account - check if account can be created
      const endpointPath = "account/verify-account";
      const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
      const urlPath = apiBaseUrl.concat("/", endpointPath);
      const response = await fetch(urlPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: data.email || "",
        }),
      });

      if (response.status === 400) {
        // Handle 400 error - show error in verification modal
        const errorData = await response.json();
        const errMsg =
          errorData.message || "There was an error verifying your account.";

        setCurrentErrors([errMsg]);
        setPopupStep("error");
        setIsSubmitting(false);
        return;
      }

      if (response.status === 200) {
        // Success - show verification code input
        setPopupStep("verification");
        setIsSubmitting(false);
        return;
      }

      // Handle other errors - show in verification modal
      setCurrentErrors(["An unexpected error occurred. Please try again."]);
      setPopupStep("error");
      setIsSubmitting(false);
    } catch (err) {
      setCurrentErrors(["Network error. Please try again."]);
      setPopupStep("error");
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (code) => {
    if (!code || code.length !== 6) {
      setCurrentErrors(["Please enter the complete 6-digit code."]);
      return;
    }

    if (!storedFormData) {
      setCurrentErrors(["Form data is missing. Please try again."]);
      return;
    }

    setIsSubmitting(true);
    setCurrentErrors([]);

    try {
      // Second call: create account with verification code
      const endpointPath = "account/create-cpa";
      const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
      const urlPath = apiBaseUrl.concat("/", endpointPath);
      const response = await fetch(urlPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          custom_user: {
            first_name: storedFormData.firstname,
            last_name: storedFormData.lastname,
            email: storedFormData.email,
            password: storedFormData.password,
          },
          password: storedFormData.password,
          confirm_password: storedFormData.confirm_password,
          license_type: storedFormData.license_type,
          termsConditions: true,
          verification_code: code,
        }),
      });

      if (response.status === 201) {
        // Parse response to get user data
        const resData = await response.json();
        const role = resData.user_role;
        const name = resData.user_name;
        const preferences = resData.preferences;
        const cpa_status = resData.account_status;
        const connected_account = resData.stripe_connected_account;
        const affiliates_enabled = resData.affiliates_enabled;
        const financial_insights = resData.financial_insights;

        // Prepare user data for Redux
        const userData = {
          user_name: name,
          user_role: role,
          preferences: preferences,
          account_status: cpa_status,
          stripe_connected_account: connected_account,
          affiliates_enabled: affiliates_enabled || false,
          financial_insights: financial_insights,
        };

        // Dispatch Redux action to update state with user data
        // Knox HTTP-only cookie is the source of truth for authentication
        store.dispatch(loginRdx(userData));

        // Fire-and-forget; must never delay or block signup.
        void associateAuthenticatedInstallationIfNative();

        // Show success message, then will auto-redirect via onSuccess
        setPopupStep("success");
        setIsSubmitting(false);
      } else {
        // Handle errors
        const errorData = await response.json();
        const errorMessage =
          errorData.message || "Failed to create account. Please try again.";
        setCurrentErrors([errorMessage]);
        setIsSubmitting(false);
      }
    } catch (err) {
      setCurrentErrors(["Network error. Please try again."]);
      setIsSubmitting(false);
    }
  };

  const handleGoToApp = () => {
    navigate("/");
  };

  const isReady =
    initialFormState !== null &&
    Array.isArray(schemaSections) &&
    schemaSections.length > 0;

  if (!isReady) {
    return <LoadingPlaceholder rows={1} type="card-large" />;
  }

  const {
    register,
    handleSubmit: handleRHFSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
    getValues,
  } = formMethods;

  return (
    <div
      className="columns"
      style={{ margin: "auto", width: "100%", maxWidth: 600 }}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleRHFSubmit(handleSubmit)(e);
        }}
      >
        <FormSectionWrapper
          title={schemaSections[0].section}
          borderReset={true}
        >
          {/* Render fields using DynamicFieldRenderer */}
          {schemaSections[0].fields.map((field) => (
            <div key={field.name}>
              {dynamicRenderField({
                field,
                formState: {},
                setFormState: () => {},
                handleToggleComposedField: () => {},
                handleValueChange: (fieldName, value) =>
                  setValue(fieldName, value),
                formMethods: {
                  register,
                  setValue,
                  getValues,
                  formState: { errors },
                  trigger,
                  watch,
                },
              })}
            </div>
          ))}
          <TermsConsentNotice />
          <button
            type="submit"
            className="cta-button primary"
            disabled={!isValid || isSubmitting}
          >
            Create Account
          </button>
        </FormSectionWrapper>
      </Form>

      {/* Verification Modal */}
      <VerificationModal
        isOpen={isPopupOpen}
        onClose={() => {
          setIsPopupOpen(false);
          setCurrentErrors([]);
          setStoredFormData(null);
        }}
        onConfirm={handleVerifyCode}
        onSuccess={handleGoToApp}
        email={userEmail}
        step={popupStep}
        isLoading={isSubmitting}
        errors={currentErrors}
        onDismissError={() => setCurrentErrors([])}
        minWidth={300}
        maxWidth={400}
      />
    </div>
  );
}

export default CreateCpaForm;
